import { browser } from "$app/environment"
import { trackLaunchDarklyFlags } from "$lib/analytics"
import * as LaunchDarkly from "launchdarkly-js-client-sdk"
import { get, readable, type Readable } from "svelte/store"
import type { FlagType } from "./flag-variants"

const LOGGED_OUT_USER_KIND = "logged_out_user"
/**
 * Options for the FlagStore.
 *
 * @template T - The type of the flag value.
 * @property {T} defaultValue - The default value for the flag.
 * @property {boolean} realtime - Whether to enable real-time updates for the flag.
 */
type FlagStoreOptions<T> = {
  defaultValue?: T
  realtime?: boolean
}

function UUID() {
  return typeof crypto === "undefined" || !crypto.randomUUID
    ? "anonymous"
    : crypto.randomUUID()
}

function mergeContexts(...contexts: LaunchDarkly.LDSingleKindContext[]) {
  return contexts.reduce<LaunchDarkly.LDMultiKindContext>(
    (acc, context) => {
      const { kind, ...rest } = context
      acc[kind] = rest
      return acc
    },
    { kind: "multi" }
  )
}

function getLoggedOutUserContext() {
  let key = localStorage.getItem(LOGGED_OUT_USER_KIND)
  if (!key) {
    key = UUID()
    localStorage.setItem(LOGGED_OUT_USER_KIND, key)
  }
  return {
    kind: LOGGED_OUT_USER_KIND,
    key,
    userAgent: navigator.userAgent,
    isTouchDevice: window.matchMedia("(pointer: coarse)").matches,
  }
}

export class TastyLDClient {
  readonly client: LaunchDarkly.LDClient
  /**
   * @param context - The LaunchDarkly context to initialize the client with.
   * @returns The initialized LaunchDarkly client.
   */
  async initialize() {
    await this.client.waitForInitialization()
    if (browser) {
      console.debug("flags", this.unwrappedFlags)
      trackLaunchDarklyFlags(this.unwrappedFlags)
    }
  }

  get flags() {
    return {
      addressDocInference: this.flagStore<boolean>(
        "ao2-address-document-inference",
        {
          defaultValue: false,
        }
      ),
      expectationsPage: this.flagStore<boolean>("ao2-expectations-page", {
        defaultValue: false,
      }),
      allowEntityAccounts: this.flagStore<boolean>("ao2-entity-accounts", {
        defaultValue: false,
      }),

      mobileLayoutOptimization: this.flagStore<boolean>(
        "ao2-mobile-layout-optimization",
        {
          defaultValue: false,
        }
      ),
      cobrowseEnabled: this.flagStore<boolean>(
        "ao2-add-cobrowsing-functionality",
        {
          defaultValue: false,
        }
      ),
      fundInAO2: this.flagStore<boolean>("ao2-plaid-and-funding", {
        defaultValue: false,
        realtime: true,
      }),
      fundOnMobile: this.flagStore<boolean>("ao2-fund-on-mobile", {
        defaultValue: false,
        realtime: true,
      }),
      resumeFunding: this.flagStore<boolean>("ao2-resume-funding-flow", {
        defaultValue: false,
        realtime: true,
      }),
      beneficialOwnersPhone: this.flagStore<boolean>(
        "ao2-beneficial-owners-phone",
        {
          defaultValue: false,
        }
      ),
    }
  }

  get unwrappedFlags() {
    return Object.fromEntries(
      Object.entries(this.flags).map(([key, flag]) => [
        key,
        get<FlagType>(flag),
      ])
    )
  }

  /**
   * Initializes the LaunchDarkly client with the provided context.
   */
  constructor(userContext?: LaunchDarkly.LDSingleKindContext) {
    const loggedOutContext = getLoggedOutUserContext()
    const context = userContext
      ? mergeContexts(loggedOutContext, userContext)
      : loggedOutContext
    this.client = LaunchDarkly.initialize(
      import.meta.env.VITE_LAUNCHDARKLY_SDK_CLIENT,
      context,
      {
        application: { id: "account-opening", version: __APP_VERSION__ },
      }
    )
  }

  /**
   * create a readable store for a feature flag.
   *
   * @template T - The type of the flag value.
   * @param {string} key - The key of the feature flag.
   * @param {FlagStoreOptions<T>} options - The options for retrieving the flag value.
   * @returns {Readable<T>} - The writable store containing the flag value.
   */
  private flagStore<T>(
    key: string,
    options: FlagStoreOptions<T> = {}
  ): Readable<T> {
    const { defaultValue, realtime } = options
    return readable(this.client.variation(key, defaultValue), (set) => {
      if (realtime) {
        this.client.on("change:" + key, set)
      }
    })
  }

  trackContactInfoCompleted() {
    this.client.track("completed-contact-info")
  }
}

// Infrastructure for adding navigation flags for dynamic routing
export type NavFlags = {} // oxlint-disable-line

/* v8 ignore start -- @preserve */
if (import.meta.vitest) {
  const { test, expect, describe, vi, beforeEach } = import.meta.vitest
  let client: TastyLDClient

  beforeEach(() => {
    vi.stubGlobal("localStorage", { getItem: () => "123" })
    vi.spyOn(LaunchDarkly, "initialize").mockImplementation(
      (_, context: LaunchDarkly.LDContext) =>
        ({
          getContext: () => context,
          variation: () => "flag",
          waitForInitialization: vi.fn(),
          allFlags: () => ({}),
          on: vi.fn(),
        }) as unknown as LaunchDarkly.LDClient
    )

    client = new TastyLDClient({ kind: "user", key: "456" })
  })

  describe("TastyLDClient", () => {
    describe("constructor", () => {
      test("initializes the LaunchDarkly client with the provided context", async () => {
        expect(client.client.getContext()).toEqual({
          kind: "multi",
          logged_out_user: {
            isTouchDevice: false,
            key: "123",
            userAgent: navigator.userAgent,
          },
          user: { key: "456" },
        })
      })
      test("initializes the LaunchDarkly client with the logged out user context if no context is provided", async () => {
        const client = new TastyLDClient()
        expect(client.client.getContext()).toEqual({
          isTouchDevice: false,
          kind: "logged_out_user",
          key: "123",
          userAgent: navigator.userAgent,
        })
      })
    })

    describe("flags", () => {
      test("returns a readable store for the feature flag", async () => {
        const flags = Object.values(client.flags)
        if (flags.length === 0) return

        expect(get(flags[0] as any)).toEqual("flag")
      })

      test("returns the unwrapped flags", async () => {
        expect(client.unwrappedFlags).toEqual({
          addressDocInference: "flag",
          expectationsPage: "flag",
          allowEntityAccounts: "flag",
          mobileLayoutOptimization: "flag",
          cobrowseEnabled: "flag",
          fundInAO2: "flag",
          fundOnMobile: "flag",
          resumeFunding: "flag",
          beneficialOwnersPhone: "flag",
        })
      })
    })

    describe("initialize", () => {
      test("waits for the client to initialize", async () => {
        await client.initialize()
        expect(client.client.waitForInitialization).toHaveBeenCalled()
      })
    })
  })
}
